import { Col, Tooltip } from 'antd';
import classNames from 'classnames';
import { flatMap, head, last, map, uniq, uniqBy } from 'lodash-es';
import { FormattedMessage } from 'react-intl';

import Whitespace from '../../../components/Whitespace';
import { DialogsParentProvider } from '../../../components/dialogs/DialogsParentContext';
import { FormItemSwitchCheckbox } from '../../../components/forms/checkable';
import { NoLabelForm } from '../../../components/forms/forms';
import { ConfigurableCard } from '../../../components/layout/cardElements';
import { FilterPopup } from '../../../containers/monitor/MonitorFilterShared';
import { getMonitorFiltersCount } from '../../../containers/monitor/monitorFilterUtils';
import { FormItemSearchSingleSelect } from '../../../containers/monitor/monitorFiltersFormItems';
import { useMonitorSearchFiltersStyles } from '../../../containers/monitor/MonitorSearchFilters.styles';
import { useRoutingsFilterStyles } from './RoutingsFilter.styles';

function RoutingFilterLabel({ labelId, value }) {
  const { styles } = useRoutingsFilterStyles();
  if (value) {
    return (
      <>
        <FormattedMessage id={labelId} />
        <Whitespace />|<Whitespace />
        <span className={styles.usedFilterLabel}>{value}</span>{' '}
      </>
    );
  }

  return <FormattedMessage id={labelId} />;
}

function RoutingsFilter({ name, labelId, options, filterValues }) {
  const isPresent = !!filterValues[name];
  return (
    <FilterPopup
      isPresent={isPresent}
      label={
        <RoutingFilterLabel labelId={labelId} value={filterValues[name]} />
      }
    >
      <FormItemSearchSingleSelect name={name} options={options} />
    </FilterPopup>
  );
}

export default function RoutingsFilters({
  form,
  routings,
  onFilterChanged,
  filteredRows,
  driveAllowed,
  driveDisabledTooltip,
}) {
  const { styles } = useMonitorSearchFiltersStyles();
  const { styles: routingsFilterStyles } = useRoutingsFilterStyles();
  const allFlights = flatMap(routings, r => r.flights);
  const airlines = uniq(map(allFlights, flight => flight.airline));

  const originAirports = uniqBy(
    map(routings, r => {
      const { originAirportCode, originAirportName } = head(r.flights) || {};
      return {
        value: originAirportCode,
        label: `${originAirportName} (${originAirportCode})`,
      };
    }),
    'value'
  );
  const destinationAirports = uniqBy(
    map(routings, r => {
      const { destinationAirportCode, destinationAirportName } =
        last(r.flights) || {};
      return {
        value: destinationAirportCode,
        label: `${destinationAirportName} (${destinationAirportCode})`,
      };
    }),
    'value'
  );

  const filterValues = form.getFieldsValue();
  const numFilters = getMonitorFiltersCount(filterValues);

  return (
    <DialogsParentProvider>
      <NoLabelForm
        form={form}
        className={classNames(
          'no-margin-form-items',
          routingsFilterStyles.quoteRoutingsFilter
        )}
        onValuesChange={(_, allFields) => onFilterChanged(allFields)}
      >
        <ConfigurableCard titleId="book.newQuote.routingsFilter.title">
          <Col
            span={8}
            className={classNames(
              'MonitorSearchFilters-CurrentState',
              styles.currentState
            )}
          >
            <span className="MonitorSearchFilters-CurrentStateBadge">
              {numFilters}
            </span>
            <span>
              <FormattedMessage
                id="filters.numberShown"
                values={{ numRows: filteredRows }}
              />
            </span>
          </Col>
          <Col span={4} className={routingsFilterStyles.routingsFilterCol}>
            <div className={routingsFilterStyles.directDriveCol}>
              <FormattedMessage id="book.newQuote.routingsFilter.showDirectDrive" />
              {!driveAllowed ? (
                <Tooltip
                  classNames={{ root: routingsFilterStyles.directDriveTooltip }}
                  title={driveDisabledTooltip}
                >
                  <FormItemSwitchCheckbox
                    disabled={!driveAllowed}
                    name="showDirectDrive"
                  />
                </Tooltip>
              ) : (
                <FormItemSwitchCheckbox
                  disabled={!driveAllowed}
                  name="showDirectDrive"
                />
              )}
            </div>
          </Col>
          <Col span={4} className={routingsFilterStyles.routingsFilterCol}>
            <RoutingsFilter
              filterValues={filterValues}
              name="airline"
              labelId="book.newQuote.routingsFilter.airlines"
              options={map(airlines, airline => ({
                value: airline,
                label: airline,
              }))}
            />
          </Col>
          <Col span={4} className={routingsFilterStyles.routingsFilterCol}>
            <RoutingsFilter
              filterValues={filterValues}
              name="departureAirport"
              labelId="book.newQuote.routingsFilter.departureAirport"
              options={originAirports}
            />
          </Col>
          <Col span={4} className={routingsFilterStyles.routingsFilterCol}>
            <RoutingsFilter
              filterValues={filterValues}
              name="arrivalAirport"
              labelId="book.newQuote.routingsFilter.arrivalAirport"
              options={destinationAirports}
            />
          </Col>
        </ConfigurableCard>
      </NoLabelForm>
    </DialogsParentProvider>
  );
}
